import { useQuery } from '@apollo/client';
import { Box, MenuItem, Select, Typography } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { ListRowHeadingValue } from '~/components/list-row';
import { LoadingPane } from '~/components/loading-pane';
import { MetaItem, SettingsBody, SettingsData, SettingsHead } from '~/components/settings';
import {
  TemplateConfigurationPageDocument,
  type TemplateConfiguration__TemplateParam as TemplateParam,
} from '../queries/configuration.generated';
import { Layout } from './components';

interface ConfigurationPanelProps {
  readonly configParam: TemplateParam;
}

const ConfigurationPanel = ({ configParam }: ConfigurationPanelProps) => {
  const { t } = useTranslation();

  return (
    <div>
      <SettingsHead>{configParam.label}</SettingsHead>
      <SettingsBody>
        <SettingsData className="last scroll no-space-between">
          <MetaItem className="first">
            <ListRowHeadingValue heading={t('common.labels.name')} value={configParam.name} />
          </MetaItem>
          <MetaItem>
            <ListRowHeadingValue
              heading={t('pages.templates.configuration.label_heading')}
              value={configParam.label}
            />
          </MetaItem>
          <MetaItem>
            <ListRowHeadingValue
              heading={t('pages.templates.configuration.kind_heading')}
              value={configParam.kind}
            />
          </MetaItem>
          <MetaItem>
            <ListRowHeadingValue
              heading={t('pages.templates.configuration.nullable_heading')}
              value={configParam.nullable}
            />
          </MetaItem>
          <MetaItem>
            <ListRowHeadingValue
              heading={t('pages.templates.configuration.default_value_heading')}
              value={configParam.defaultValue}
            />
          </MetaItem>
          <MetaItem>
            <ListRowHeadingValue
              heading={t('common.labels.description')}
              value={configParam.description}
            />
          </MetaItem>
        </SettingsData>
      </SettingsBody>
    </div>
  );
};

export const TemplateConfiguration = () => {
  const { t } = useTranslation();
  const params = useParams<{ templateId: string }>();
  const templateId = Number(params.templateId);

  const { data, loading } = useQuery(TemplateConfigurationPageDocument, {
    variables: { templateId },
  });

  const versions = data?.template?.templateVersions.nodes ?? [];

  // Keyed by id so a cleanup that removes the picked version falls back to the latest.
  const [selectedId, setSelectedId] = useState<number>();
  const selected = versions.find((item) => item.id === selectedId) ?? versions[0];

  return (
    <Layout>
      <LoadingPane in={loading && !data} size={40} thickness={4}>
        {data?.template == null ? null : versions.length === 0 ? (
          <Typography variant="h4">{t('pages.templates.configuration.no_versions')}</Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Select
              inputProps={{ 'aria-label': t('pages.templates.configuration.version_label') }}
              fullWidth
              value={selected.id}
              onChange={(event) => setSelectedId(Number(event.target.value))}
            >
              {versions.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.version}
                </MenuItem>
              ))}
            </Select>

            {selected.templateParams.map((param) => (
              <ConfigurationPanel configParam={param} key={param.id} />
            ))}
          </Box>
        )}
      </LoadingPane>
    </Layout>
  );
};
